// ============================================
// Biomarker Correlation Rules
// ============================================
//
// When a primary biomarker is abnormal, correlated
// biomarkers adjust to produce clinically plausible
// synthetic panels. Each rule encodes the clinical
// relationship, strength, and the adjustment function
// the correlation engine should apply.
// ============================================

import type { BiomarkerCorrelation } from '../../types/patientJourney';

export const BIOMARKER_CORRELATIONS: BiomarkerCorrelation[] = [
  // 1. TSH <-> Free T4 (inverse, strong)
  {
    primaryBiomarker: 'TSH',
    correlatedBiomarker: 'Free T4',
    relationship: 'inverse',
    strength: 'strong',
    rule: 'Elevated TSH with low FT4 = primary hypothyroidism. Suppressed TSH with high FT4 = hyperthyroidism.',
    adjustmentFn: 'inverseTSH_FT4',
  },

  // 2. Estradiol <-> FSH (inverse, strong)
  {
    primaryBiomarker: 'Estradiol',
    correlatedBiomarker: 'FSH',
    relationship: 'inverse',
    strength: 'strong',
    rule: 'Low estradiol drives FSH up via pituitary negative feedback. Declining ovarian reserve shows elevated FSH with low estradiol.',
    adjustmentFn: 'inverseEstradiol_FSH',
  },

  // 3. Fasting Insulin <-> Fasting Glucose (positive, moderate)
  {
    primaryBiomarker: 'Fasting Insulin',
    correlatedBiomarker: 'Fasting Glucose',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Insulin resistance drives both fasting insulin and glucose upward. Elevated insulin with normal glucose = compensated IR.',
    adjustmentFn: 'insulinResistancePattern',
  },

  // 4. Fasting Insulin <-> HbA1c (conditional, moderate)
  {
    primaryBiomarker: 'Fasting Insulin',
    correlatedBiomarker: 'Hemoglobin A1c',
    relationship: 'conditional',
    strength: 'moderate',
    rule: 'Elevated insulin with normal HbA1c = early insulin resistance (compensated). Elevated insulin with elevated HbA1c = decompensated IR / pre-diabetes.',
    adjustmentFn: 'conditionalInsulin_HbA1c',
  },

  // 5. Vitamin D <-> Calcium (positive, moderate)
  {
    primaryBiomarker: 'Vitamin D (25-OH)',
    correlatedBiomarker: 'Calcium',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Low vitamin D impairs calcium absorption, potentially lowering serum calcium. Severe deficiency triggers PTH-mediated bone resorption.',
    adjustmentFn: 'positiveVitD_Calcium',
  },

  // 6. hsCRP <-> Ferritin (positive, moderate)
  {
    primaryBiomarker: 'hs-CRP',
    correlatedBiomarker: 'Ferritin',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Systemic inflammation elevates ferritin as an acute phase reactant. Elevated ferritin in the context of high hsCRP may not reflect true iron stores.',
    adjustmentFn: 'inflammationFerritin',
  },

  // 7. Testosterone Total <-> SHBG (conditional, strong)
  {
    primaryBiomarker: 'Testosterone Total',
    correlatedBiomarker: 'SHBG',
    relationship: 'conditional',
    strength: 'strong',
    rule: 'High SHBG with normal total testosterone = low free testosterone (bioavailable). Low SHBG with normal total T = artificially elevated free T.',
    adjustmentFn: 'conditionalTestosterone_SHBG',
  },

  // 8. TSH <-> Free T3 (inverse, moderate)
  {
    primaryBiomarker: 'TSH',
    correlatedBiomarker: 'Free T3',
    relationship: 'inverse',
    strength: 'moderate',
    rule: 'Elevated TSH suppresses T3 production. Poor T4-to-T3 conversion may show normal TSH with low T3.',
    adjustmentFn: 'inverseTSH_FT3',
  },

  // 9. Iron <-> Hemoglobin (positive, strong)
  {
    primaryBiomarker: 'Iron',
    correlatedBiomarker: 'Hemoglobin',
    relationship: 'positive',
    strength: 'strong',
    rule: 'Low serum iron limits hemoglobin synthesis, producing iron-deficiency anemia. Chronic low iron progressively drops hemoglobin.',
    adjustmentFn: 'positiveIron_Hemoglobin',
  },

  // 10. Iron <-> RDW (inverse, moderate)
  {
    primaryBiomarker: 'Iron',
    correlatedBiomarker: 'RDW',
    relationship: 'inverse',
    strength: 'moderate',
    rule: 'Iron deficiency causes increased red cell size variability (elevated RDW) as new microcytic cells mix with normocytic cells.',
    adjustmentFn: 'inverseIron_RDW',
  },

  // 11. Ferritin <-> Iron (positive, moderate)
  {
    primaryBiomarker: 'Ferritin',
    correlatedBiomarker: 'Iron',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Ferritin reflects stored iron. Low ferritin = depleted iron stores, usually preceding low serum iron.',
    adjustmentFn: 'positiveFerritin_Iron',
  },

  // 12. BUN <-> Creatinine (positive, moderate)
  {
    primaryBiomarker: 'BUN',
    correlatedBiomarker: 'Creatinine',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Both rise with declining renal function. Elevated BUN:creatinine ratio may indicate pre-renal azotemia (dehydration).',
    adjustmentFn: 'positiveBUN_Creatinine',
  },

  // 13. ALT <-> AST (positive, strong)
  {
    primaryBiomarker: 'ALT',
    correlatedBiomarker: 'AST',
    relationship: 'positive',
    strength: 'strong',
    rule: 'Liver transaminases rise together in hepatocellular damage. ALT > AST suggests non-alcoholic fatty liver; AST > ALT suggests alcoholic liver disease.',
    adjustmentFn: 'positiveALT_AST',
  },

  // 14. ALT <-> GGT (positive, moderate)
  {
    primaryBiomarker: 'ALT',
    correlatedBiomarker: 'GGT',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'GGT rises with ALT in hepatobiliary damage. Isolated GGT elevation may indicate alcohol use or medication effect.',
    adjustmentFn: 'positiveALT_GGT',
  },

  // 15. Total Cholesterol <-> LDL (positive, strong)
  {
    primaryBiomarker: 'Total Cholesterol',
    correlatedBiomarker: 'LDL',
    relationship: 'positive',
    strength: 'strong',
    rule: 'LDL is the major component of total cholesterol. Elevated total cholesterol is primarily driven by LDL in most patients.',
    adjustmentFn: 'positiveTotalChol_LDL',
  },

  // 16. HDL <-> Triglycerides (inverse, moderate)
  {
    primaryBiomarker: 'HDL',
    correlatedBiomarker: 'Triglycerides',
    relationship: 'inverse',
    strength: 'moderate',
    rule: 'Low HDL with elevated triglycerides = metabolic syndrome pattern. Insulin resistance drives TG up and HDL down.',
    adjustmentFn: 'inverseHDL_Triglycerides',
  },

  // 17. Fasting Glucose <-> HbA1c (positive, strong)
  {
    primaryBiomarker: 'Fasting Glucose',
    correlatedBiomarker: 'Hemoglobin A1c',
    relationship: 'positive',
    strength: 'strong',
    rule: 'Both reflect glycemic control. Fasting glucose is acute, HbA1c is 90-day average. Concordant elevation confirms hyperglycemia.',
    adjustmentFn: 'positiveGlucose_HbA1c',
  },

  // 18. Estradiol <-> Progesterone (conditional, moderate)
  {
    primaryBiomarker: 'Estradiol',
    correlatedBiomarker: 'Progesterone',
    relationship: 'conditional',
    strength: 'moderate',
    rule: 'Cycle-phase dependent. Low estradiol in follicular phase may predict poor ovulation and low luteal progesterone. Anovulatory cycles show low progesterone regardless.',
    adjustmentFn: 'conditionalEstradiol_Progesterone',
  },

  // 19. Testosterone <-> LH (inverse, moderate)
  {
    primaryBiomarker: 'Testosterone Total',
    correlatedBiomarker: 'LH',
    relationship: 'inverse',
    strength: 'moderate',
    rule: 'Low testosterone triggers pituitary LH release via negative feedback. Primary hypogonadism shows high LH with low T; secondary shows low LH with low T.',
    adjustmentFn: 'inverseTestosterone_LH',
  },

  // 20. Cortisol <-> DHEA-S (inverse, weak)
  {
    primaryBiomarker: 'Cortisol AM',
    correlatedBiomarker: 'DHEA-S',
    relationship: 'inverse',
    strength: 'weak',
    rule: 'Chronic cortisol elevation (HPA axis dysregulation) shifts adrenal output away from DHEA-S. High cortisol with low DHEA-S = adrenal fatigue pattern.',
    adjustmentFn: 'inverseCortisol_DHEAS',
  },

  // 21. B12 <-> Homocysteine (inverse, moderate)
  {
    primaryBiomarker: 'B12',
    correlatedBiomarker: 'Homocysteine',
    relationship: 'inverse',
    strength: 'moderate',
    rule: 'B12 is a cofactor in homocysteine metabolism. B12 deficiency causes homocysteine accumulation, a cardiovascular risk factor.',
    adjustmentFn: 'inverseB12_Homocysteine',
  },

  // 22. Folate <-> Homocysteine (inverse, moderate)
  {
    primaryBiomarker: 'Folate',
    correlatedBiomarker: 'Homocysteine',
    relationship: 'inverse',
    strength: 'moderate',
    rule: 'Folate is required for homocysteine remethylation. Folate deficiency elevates homocysteine independent of B12 status.',
    adjustmentFn: 'inverseFolate_Homocysteine',
  },

  // 23. Vitamin D <-> PTH (inverse, strong)
  {
    primaryBiomarker: 'Vitamin D (25-OH)',
    correlatedBiomarker: 'PTH',
    relationship: 'inverse',
    strength: 'strong',
    rule: 'Low vitamin D triggers PTH secretion to maintain calcium homeostasis. Secondary hyperparathyroidism is driven by vitamin D deficiency.',
    adjustmentFn: 'inverseVitD_PTH',
  },

  // 24. Albumin <-> Total Protein (positive, moderate)
  {
    primaryBiomarker: 'Albumin',
    correlatedBiomarker: 'Total Protein',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Albumin constitutes ~60% of total protein. Low albumin from malnutrition or liver disease pulls total protein down.',
    adjustmentFn: 'positiveAlbumin_TotalProtein',
  },

  // 25. WBC <-> Neutrophils (positive, strong)
  {
    primaryBiomarker: 'WBC',
    correlatedBiomarker: 'Neutrophils',
    relationship: 'positive',
    strength: 'strong',
    rule: 'Neutrophils comprise 40-70% of total WBC. Elevated WBC is most commonly driven by neutrophilia (infection, stress response).',
    adjustmentFn: 'positiveWBC_Neutrophils',
  },

  // 26. Triglycerides <-> LDL Particle Number (positive, moderate)
  {
    primaryBiomarker: 'Triglycerides',
    correlatedBiomarker: 'LDL Particle Number',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Elevated triglycerides drive formation of small dense LDL particles, increasing LDL-P even when LDL-C appears normal.',
    adjustmentFn: 'positiveTG_LDLP',
  },

  // 27. Hemoglobin <-> RBC (positive, strong)
  {
    primaryBiomarker: 'Hemoglobin',
    correlatedBiomarker: 'RBC',
    relationship: 'positive',
    strength: 'strong',
    rule: 'Hemoglobin is carried by RBCs. Anemia shows concordant drops in both. Polycythemia shows concordant elevation.',
    adjustmentFn: 'positiveHemoglobin_RBC',
  },

  // 28. Fasting Insulin <-> HOMA-IR (positive, strong)
  {
    primaryBiomarker: 'Fasting Insulin',
    correlatedBiomarker: 'HOMA-IR',
    relationship: 'positive',
    strength: 'strong',
    rule: 'HOMA-IR is calculated from fasting insulin and glucose. Elevated insulin directly raises HOMA-IR.',
    adjustmentFn: 'positiveInsulin_HOMAIR',
  },

  // 29. Creatinine <-> eGFR (inverse, strong)
  {
    primaryBiomarker: 'Creatinine',
    correlatedBiomarker: 'eGFR',
    relationship: 'inverse',
    strength: 'strong',
    rule: 'eGFR is inversely derived from creatinine. Rising creatinine indicates declining glomerular filtration rate.',
    adjustmentFn: 'inverseCreatinine_eGFR',
  },

  // 30. hs-CRP <-> ESR (positive, moderate)
  {
    primaryBiomarker: 'hs-CRP',
    correlatedBiomarker: 'ESR',
    relationship: 'positive',
    strength: 'moderate',
    rule: 'Both are inflammatory markers. CRP rises and falls rapidly; ESR changes more slowly. Concordant elevation confirms active inflammation.',
    adjustmentFn: 'positiveHsCRP_ESR',
  },
];
